<template>
    <div class="analytics-placeholder">
        <h2 class="title">
            {{ title }}
        </h2>
        <p class="content text" v-html="content" /><!-- eslint-disable-line vue/no-v-html -->
    </div>
</template>
<script>
// External dependencies
import { mapState } from 'vuex';
import { filter } from 'lodash';
// Internal dependencies
import { ImportStatus, hasImportStatus } from '@/utils/show';

export default {
    props: {
        currentShowHash: {
            type: String,
            default: null,
        },
    },

    data: () => ({
        supportUrl: 'mailto:help@fusebox.fm',
        directoryListingUrl: 'https://support.fusebox.fm/article/506-get-your-podcast-listed',
    }),

    computed: {
        ...mapState(['shows']),

        /**
         * Gets the object of current show.
         *
         * @returns {object|null} Current show.
         */
        currentShow() {
            return this.$store.state.shows[this.currentShowHash] ?? null;
        },

        /**
         * Gets the component title.
         *
         * @returns {string} Component title.
         */
        title() {
            const titles = {
                default: 'Quickly view your listens here.',
                [ImportStatus.Error]: 'Oops! Looks like we hit a snag.',
                [ImportStatus.Pending]: 'We’re migrating your podcast episodes.',
            };

            return titles[this.importStatus] ?? titles.default;
        },

        /**
         * Gets the component content template.
         *
         * @returns {string} Component content template.
         */
        contentTemplate() {
            const templates = {
                default: 'Your analytics are little empty right now. Make sure your podcast is listed on at least one podcast directory — <a href="{directoryListingUrl}">Learn more</a>.',
                [ImportStatus.Error]: 'We’re having trouble getting the information we need from your previous host. Contact our support team to get back on track — <a href="{supportUrl}">Contact Us</a>',
                [ImportStatus.Pending]: 'Once we’ve completed the migration, make sure to update any podcast directory with your new RSS feed — <a href="{directoryListingUrl}">Learn more</a>.',
            };

            return templates[this.importStatus] ?? templates.default;
        },

        /**
         * Gets the import status.
         *
         * There are two possible results from this method:
         * - if there is current show then return imports status of this show
         * - if there is no current show then iterate over each show of current
         *   user and determine whether any of them has error or pending status
         *
         * @returns {number|null} Import status.
         */
        importStatus() {
            if (this.currentShow) {
                return this.currentShow.importStatus;
            }

            return this.hasShowsWithImportError
                ? ImportStatus.Error
                : this.hasImportingShows
                    ? ImportStatus.Pending
                    : null;
        },

        /**
         * Checks whether user has any show which is importing.
         *
         * @returns {boolean} Check result.
         */
        hasImportingShows() {
            return 0 < Object.values(this.showsWithImportStatus(ImportStatus.Pending)).length;
        },

        /**
         * Checks whether user has any show with import error.
         *
         * @returns {boolean} Check result.
         */
        hasShowsWithImportError() {
            return 0 < Object.values(this.showsWithImportStatus(ImportStatus.Error)).length;
        },

        /**
         * Returns computed text content with support link.
         *
         * @returns {string} Content.
         */
        content() {
            return this.contentTemplate
                .replace('{supportUrl}', this.supportUrl)
                .replace('{directoryListingUrl}', this.directoryListingUrl);
        },
    },

    methods: {
        /**
         * Filters shows by import status value.
         *
         * @param   {number} importStatus Import status value.
         * @returns {object}              Collection of shows.
         */
        showsWithImportStatus(importStatus) {
            return filter(this.shows, (show) => hasImportStatus(show, importStatus));
        },
    },
};
</script>
